import { FC, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useTicketDetail } from 'features/CustomFieldsResponses/hooks/useEventCustomFields';
import { getData } from 'services/api';
import { IdParamsType } from 'utils/types';
import { isEmbeddedInIframe } from 'utils/utils';

import { ResponsesData } from '../../types';

interface SwitchTicketDelegatorProps {
	data: ResponsesData;
	onDone: () => void;
	transactionTickets: Record<string, number[]>;
}

interface EventLite {
	id: number;
	name: string;
}

/**
 * Posts an `open-switch-ticket` message to the Punchup dashboard once the
 * sibling/transaction data is loaded, then unmounts. Standalone tixologist
 * (no iframe parent) is a no-op — the Switch Ticket flow only exists in the
 * Punchup app.
 */
export const SwitchTicketDelegator: FC<SwitchTicketDelegatorProps> = ({ data, onDone, transactionTickets }) => {
	const { id: routeEventIdParam } = useParams<IdParamsType>();
	/* On a festival-wide attendee list a row can belong to another show than the page's, so the row's own show is
	 * the switch's source; the route is the fallback for rows that do not carry it. */
	const currentEventId = data.event_id ?? (routeEventIdParam ? Number(routeEventIdParam) : undefined);
	const queryClient = useQueryClient();
	const sentRef = useRef(false);

	const { data: sourceTicketDetail } = useTicketDetail(data.ticket_id);
	const { data: sourceEvent } = useQuery<EventLite | null>({
		queryKey: ['event-detail', currentEventId],
		queryFn: () => (currentEventId ? getData(`/events/${currentEventId}`) : Promise.resolve(null)),
		enabled: !!currentEventId && !data.event_name,
	});
	const currentEventName = data.event_name ?? sourceEvent?.name ?? null;

	useEffect(() => {
		if (sentRef.current) return;
		if (!isEmbeddedInIframe()) {
			sentRef.current = true;
			onDone();
			return;
		}
		// Wait until ticket detail has resolved — undefined means in-flight,
		// null means "no detail" (rare but still proceed).
		if (sourceTicketDetail === undefined) return;

		const sourceTransactionId: number | null =
			sourceTicketDetail?.last_transaction_id ?? sourceTicketDetail?.transaction_id ?? null;

		const cachedResponses = queryClient.getQueriesData({ queryKey: ['responses'] });
		const allRows: ResponsesData[] = [];
		for (const [, cached] of cachedResponses) {
			if (cached && typeof cached === 'object' && 'pages' in (cached as Record<string, unknown>)) {
				const pages = (cached as { pages: unknown[] }).pages;
				for (const page of pages) {
					if (Array.isArray(page)) allRows.push(...(page as ResponsesData[]));
				}
			}
		}
		const isOnAnotherShow = (row: ResponsesData) =>
			data.event_id !== undefined && row.event_id !== undefined && row.event_id !== data.event_id;
		const otherShowTicketIds = new Set(
			allRows.filter((row) => row.order_id === data.order_id && isOnAnotherShow(row)).map((row) => row.ticket_id)
		);
		const seen = new Set<number>();
		const siblings: Array<{
			ticketId: number;
			ticketTypeName?: string;
			seatId: string | null;
			amount?: number;
			/** Display status (GetStatus / 'pending_upgrade') so the dashboard modal can gate ineligible rows. */
			status?: string;
		}> = [];
		for (const row of allRows) {
			if (row.order_id !== data.order_id || isOnAnotherShow(row)) continue;
			if (seen.has(row.ticket_id)) continue;
			seen.add(row.ticket_id);
			siblings.push({
				ticketId: row.ticket_id,
				ticketTypeName: row.ticket_types_string,
				seatId: row.seat_id ?? null,
				amount: row.amount,
				status: row.status,
			});
		}

		sentRef.current = true;
		window.parent.postMessage(
			{
				action: 'open-switch-ticket',
				payload: {
					ticketIds: (transactionTickets[data.order_id] ?? [data.ticket_id]).filter(
						(id) => !otherShowTicketIds.has(id)
					),
					orderId: data.order_id,
					transactionId: sourceTransactionId,
					currentEventId: currentEventId ?? null,
					currentEventName,
					source: {
						ticketTypeName: data.ticket_types_string,
						seatId: data.seat_id ?? null,
						amount: data.amount,
						status: data.status,
					},
					fan: {
						fullName: data.full_name,
						email: data.email,
					},
					siblings,
				},
			},
			'*'
		);
		onDone();
	}, [data, currentEventId, currentEventName, sourceTicketDetail, queryClient, onDone, transactionTickets]);

	return null;
};
